import { useEffect, useState } from 'react';
import api from '@/lib/api';

const fmtDt = (d) => {
  if (!d) return 'N/A';
  try {
    return new Date(d).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return d; }
};

const STATE_STYLE = {
  Current:   { color: '#03f37c' },
  Approved:  { color: '#03f37c' },
  Escalated: { color: '#000000', fontWeight: '600' },
  Pending:   { color: '#a3a3a3' },
  Held:      { color: '#737373' },
  Rejected:  { color: '#737373' },
};

export default function Audit() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get('/audit/events')
      .then(r => setEvents(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="font-body text-gray-400 py-8">Loading audit log…</p>;
  if (error)   return <p className="font-body text-gray-700 py-8">Error: {error}</p>;

  return (
    <div data-testid="audit-page">
      <h1 className="page-heading mb-1">Audit</h1>
      <p className="font-body text-gray-500 mb-2" style={{ fontSize: '15px', lineHeight: 1.5 }}>
        A record of reviews and decisions.
      </p>
      <p className="font-body text-gray-400 mb-8" style={{ fontSize: '13px' }}>
        This log is append-only. Events cannot be edited or deleted.
      </p>

      {events.length === 0 ? (
        <p className="font-body text-gray-400">No audit events recorded.</p>
      ) : (
        <div data-testid="audit-events-list">
          {/* Desktop column headers */}
          <div
            className="ky-table-wrapper hidden md:grid gap-4 px-4 pb-2 mb-2"
            style={{
              gridTemplateColumns: '160px 1fr 1fr 120px 100px',
              borderBottom: '1px solid #e5e5e5'
            }}
          >
            {['Timestamp', 'Actor', 'Action / Object', 'Reason', 'State'].map(h => (
              <span key={h} className="font-heading text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {h}
              </span>
            ))}
          </div>

          {/* Desktop rows */}
          <div className="ky-table-wrapper hidden md:block space-y-0">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="border-b border-gray-100 py-4 px-4"
                data-testid={`audit-event-${ev.id}`}
              >
                <div
                  className="grid gap-4 items-start"
                  style={{ gridTemplateColumns: '160px 1fr 1fr 120px 100px' }}
                >
                  <p className="font-body text-xs text-gray-400">{fmtDt(ev.timestamp)}</p>
                  <div>
                    <p className="font-body text-sm text-black font-medium">{ev.actor}</p>
                    <p className="font-body text-xs text-gray-400">{ev.role}</p>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-black text-sm">{ev.action}</p>
                    <p className="font-body text-xs text-gray-500 mt-0.5">{ev.object_ref}</p>
                  </div>
                  <p className="font-body text-xs text-gray-400 leading-relaxed">
                    {ev.reason || <span className="italic">No note</span>}
                  </p>
                  <span
                    className="font-heading text-xs font-semibold"
                    style={STATE_STYLE[ev.resulting_state] || { color: '#737373' }}
                  >
                    {ev.resulting_state}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile cards */}
          <div className="ky-mobile-card space-y-0">
            {events.map((ev) => (
              <div
                key={`m-${ev.id}`}
                className="ky-mobile-card-item"
                data-testid={`audit-event-mobile-${ev.id}`}
              >
                {/* Timestamp + state */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="font-body text-gray-400" style={{ fontSize: '13px' }}>{fmtDt(ev.timestamp)}</p>
                  <span
                    className="font-heading font-semibold shrink-0"
                    style={{ fontSize: '13px', ...(STATE_STYLE[ev.resulting_state] || { color: '#737373' }) }}
                  >
                    {ev.resulting_state}
                  </span>
                </div>
                {/* Actor */}
                <p className="font-body font-medium text-black" style={{ fontSize: '14px', lineHeight: 1.4 }}>
                  {ev.actor}
                  <span className="font-normal text-gray-400"> · {ev.role}</span>
                </p>
                {/* Action + object */}
                <p className="font-heading font-semibold text-black mt-1" style={{ fontSize: '15px' }}>
                  {ev.action}
                </p>
                <p className="font-body text-gray-500 mt-0.5" style={{ fontSize: '14px' }}>
                  {ev.object_ref}
                </p>
                {/* Reason */}
                {ev.reason && (
                  <p className="font-body text-gray-400 mt-1.5 italic leading-relaxed" style={{ fontSize: '13px' }}>
                    {ev.reason}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
